import { Box } from "@mantine/core";

import { ROOT_DOMAIN } from "../../../config/endpoints";

/**
 * StudioProps configure the {% studio %} tag — a live Map Embedding iframe.
 * The fields mirror the embed search params (`@mountayaapp/core` studio search
 * schema): content selection ids, the terrain/overlay map state, the
 * `preferred_*` viewer preferences, and the `with_*` UI toggles. `height` and
 * `title` frame the iframe itself; `publishable_key` overrides the demo key;
 * `view` seeds the initial map view through the URL hash. Every studio param is
 * optional so a bare {% studio %} renders the default demo map.
 */
interface StudioProps {
  activity?: string;
  collection_id?: string;
  enrichments?: string[];
  height?: number;
  itinerary_id?: string;
  overlay?: string;
  preferred_language?: string;
  preferred_theme?: string;
  preferred_unit_length?: string;
  preferred_unit_slope?: string;
  preferred_unit_temperature?: string;
  publishable_key?: string;
  terrain?: string;
  terrain_variant?: string;
  time_offset?: number;
  title?: string;
  view?: string;
  with_directions_control?: boolean;
  with_download_control?: boolean;
  with_navigation_control?: boolean;
  with_overlays?: string[];
  with_overlays_control?: boolean;
  with_preferences_control?: boolean;
  with_profile_analysis?: boolean;
  with_profile_characteristics?: boolean;
  with_profile_elevation?: boolean;
  with_streetview_control?: boolean;
  with_terrains?: string[];
  zone_id?: string;
}

// Origin-scoped demo publishable key for the documentation embeds. Authors can
// override it per embed; this default lets a bare {% studio %} render a live map.
const DEMO_PUBLISHABLE_KEY = "pk_d5947e9275121da0ebeacb6de4ca6552";

// The Map Embedding route on the application root.
const EMBED_PATH = "/studio/embed";

/**
 * buildStudioUrl assembles the Map Embedding iframe `src` from the studio
 * params. The publishable key leads (the demo key unless overridden); every
 * other present param becomes a query param, with arrays expanded to repeated
 * params; `view` is excluded from the query and instead placed in the URL hash
 * (`#view=…`), where the studio reads the initial MapLibre view. Absent params
 * are dropped so the embed falls back to its own defaults.
 */
const buildStudioUrl = (params: Omit<StudioProps, "height" | "title">): string => {
  const { publishable_key, view, ...rest } = params;

  const query = new URLSearchParams();
  query.append("publishable_key", publishable_key ?? DEMO_PUBLISHABLE_KEY);

  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        query.append(key, String(entry));
      }
    } else {
      query.append(key, String(value));
    }
  }

  const base = `${ROOT_DOMAIN}${EMBED_PATH}?${query.toString()}`;

  return view ? `${base}#view=${view}` : base;
};

/**
 * Studio is the {% studio %} renderer the documentation app injects. It renders
 * a live Map Embedding iframe — a static element with a build-time URL, so it
 * is server-rendering safe and needs no client-only gate; the browser defers
 * loading until the frame nears the viewport.
 */
export const Studio = ({ height = 480, title, ...params }: StudioProps) => (
  // mantine:exempt — framed embed: a 1px theme-border, a theme radius, and a
  // full-width fill on the iframe; Mantine has no border-side shortcut prop.
  <Box
    component="iframe"
    height={height}
    loading="lazy"
    my="md"
    src={buildStudioUrl(params)}
    style={{
      border: "1px solid var(--mantine-color-default-border)",
      borderRadius: "var(--mantine-radius-md)",
      width: "100%",
    }}
    title={title}
  />
);
